import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { normalizeUser } from '../auth/normalizeUser';

// normalizeUser() is shared with the auth flows and deliberately doesn't
// carry created_at/updated_at (a session's own user object never needs
// them). The Admin Management detail/list views do want to show them, so
// this wraps normalizeUser with those two fields passed through as-is
// rather than growing the shared auth normalizer for an admin-only need.
function normalizeAdmin(raw) {
  const user = normalizeUser(raw);
  if (!user) return user;
  return { ...user, created_at: raw?.created_at ?? null, updated_at: raw?.updated_at ?? null };
}

/**
 * Real-backend "Managing administrator accounts" surface (super-admin only)
 * — https://cavan-investment-app-backend.onrender.com/docs, tag "Admin".
 * Talks to the real backend via authBaseQuery/authAxios, same as authApi.js
 * — this is the second feature slice (after auth) wired to the live API;
 * everything else in the app still targets the mock server (see
 * app/axiosBaseQuery.js) because Investors/Portfolios/Transactions/Reports
 * are explicitly "not yet populated" server-side.
 *
 * Supersedes the old mock-backed `features/admin/adminUsersApi.js`, which
 * nothing else in the app references anymore.
 */
export const adminAccountsApi = createApi({
  reducerPath: 'adminAccountsApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['AdminAccount'],
  endpoints: (builder) => ({
    // GET /admin/admins — accounts holding the admin role only; investors
    // and super-admins are both excluded server-side.
    listAdmins: builder.query({
      query: () => ({ url: '/admin/admins', method: 'GET' }),
      transformResponse: (response) => (response?.admins ?? []).map(normalizeAdmin),
      providesTags: (result = []) => [
        ...result.map(({ id }) => ({ type: 'AdminAccount', id })),
        { type: 'AdminAccount', id: 'LIST' },
      ],
    }),

    // GET /admin/admins/{uuid} — detail view. 404 is returned identically
    // whether the uuid doesn't exist, belongs to an investor, or belongs to
    // a super-admin — don't try to tell those apart, just show "not found"
    // (see authErrorMessage's notFound override, used wherever this is
    // called).
    getAdmin: builder.query({
      query: (uuid) => ({ url: `/admin/admins/${uuid}`, method: 'GET' }),
      transformResponse: (response) => normalizeAdmin(response?.user),
      providesTags: (result, error, uuid) => [{ type: 'AdminAccount', id: uuid }],
    }),

    // POST /admin/admins — always creates an `admin` (never `super-admin`;
    // a role field would be ignored server-side, so we never send one).
    // Password is generated server-side and emailed to the new admin along
    // with must_change_password: true — never accepted from or returned to
    // the caller.
    createAdmin: builder.mutation({
      query: (body) => ({ url: '/admin/admins', method: 'POST', data: body }),
      transformResponse: (response) => normalizeAdmin(response?.user),
      invalidatesTags: [{ type: 'AdminAccount', id: 'LIST' }],
    }),

    // PUT /admin/admins/{uuid} — only first_name/last_name/phone are ever
    // applied; email/password/role have no validation rule on this route
    // and are silently dropped server-side, so we don't bother sending them.
    updateAdmin: builder.mutation({
      query: ({ uuid, ...body }) => ({ url: `/admin/admins/${uuid}`, method: 'PUT', data: body }),
      transformResponse: (response) => normalizeAdmin(response?.user),
      invalidatesTags: (result, error, { uuid }) => [
        { type: 'AdminAccount', id: uuid },
        { type: 'AdminAccount', id: 'LIST' },
      ],
    }),

    // DELETE /admin/admins/{uuid} — hard delete; every refresh token for
    // the account is revoked as part of the same operation.
    deleteAdmin: builder.mutation({
      query: (uuid) => ({ url: `/admin/admins/${uuid}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'AdminAccount', id: 'LIST' }],
    }),

    // POST /admin/admins/{uuid}/revoke — strips the admin role only; the
    // account itself is untouched (still logs in fine, just as an
    // investor) and no session revocation happens here — the 2FA gate and
    // every manage-*-permission check are evaluated live per request, so
    // losing the role takes effect immediately regardless of any token the
    // account still holds. Distinct action from delete — not a toggle.
    revokeAdmin: builder.mutation({
      query: (uuid) => ({ url: `/admin/admins/${uuid}/revoke`, method: 'POST' }),
      transformResponse: (response) => normalizeAdmin(response?.user),
      invalidatesTags: [{ type: 'AdminAccount', id: 'LIST' }],
    }),

    // POST /admin/users/{uuid}/make-admin — grants the admin role to any
    // account not already admin/super-admin (a lifelong investor and a
    // previously-revoked admin are handled identically). Revokes every
    // existing refresh token for the account, forcing its next request
    // through a full login — the only point the 2FA gate actually runs —
    // rather than letting an already-live session keep minting access
    // tokens without ever completing 2FA. Lives under users/, not
    // admins/, because the target usually isn't an admin yet.
    makeAdmin: builder.mutation({
      query: (uuid) => ({ url: `/admin/users/${uuid}/make-admin`, method: 'POST' }),
      transformResponse: (response) => normalizeAdmin(response?.user),
      invalidatesTags: [{ type: 'AdminAccount', id: 'LIST' }],
    }),

    // POST /admin/users/{uuid}/unlock — clears failed_login_attempts and
    // locked_until on the target account. Works on investor and
    // admin/super-admin targets alike (the permission required differs by
    // target role server-side; a super-admin caller bypasses both checks).
    // There's no list endpoint that exposes lock state today, so this is
    // offered as a standalone action rather than a per-row toggle.
    unlockUser: builder.mutation({
      query: (uuid) => ({ url: `/admin/users/${uuid}/unlock`, method: 'POST' }),
    }),
  }),
});

export const {
  useListAdminsQuery,
  useGetAdminQuery,
  useLazyGetAdminQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useRevokeAdminMutation,
  useMakeAdminMutation,
  useUnlockUserMutation,
} = adminAccountsApi;
